import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ProgramContentSlideOwnerRef } from '../../types/programContentSlideCanvas';
import { fetchSlideOwnerStatsMap } from '../../services/programPresentationListService';
import {
  readProgramPresentationListStatsCache,
  type SlideOwnerStats,
} from '../../utils/programPresentationListStatsCache';
import { programContentSlidePresenceKey } from '../../services/programContentSlideService';
import { schoolKeys } from '../keys';
import { lsInitialDataOptions } from './lsInitialData';

export type UsePresentationOwnerStatsQueryParams = {
  workspaceId: string;
  owners: ProgramContentSlideOwnerRef[];
  ownerKeysSignature: string;
  enabled?: boolean;
  forceRefresh?: boolean;
};

function statsMapFromRecord(
  workspaceId: string,
  ownerKeys: string[]
): { found: boolean; map: Map<string, SlideOwnerStats> } {
  if (!ownerKeys.length) return { found: true, map: new Map() };
  const cached = readProgramPresentationListStatsCache(workspaceId, ownerKeys);
  if (!ownerKeys.every((key) => cached.has(key))) {
    return { found: false, map: cached };
  }
  return { found: true, map: cached };
}

export function usePresentationOwnerStatsQuery(
  params: UsePresentationOwnerStatsQueryParams
): UseQueryResult<Map<string, SlideOwnerStats>> {
  const {
    workspaceId,
    owners,
    ownerKeysSignature,
    enabled = true,
    forceRefresh = false,
  } = params;

  const ownerKeys = owners.map((o) => programContentSlidePresenceKey(o));
  const ls = statsMapFromRecord(workspaceId, ownerKeys);

  return useQuery<Map<string, SlideOwnerStats>>({
    queryKey: schoolKeys.presentationOwnerStats(workspaceId, ownerKeysSignature),
    enabled: enabled && !!workspaceId.trim() && owners.length > 0,
    ...lsInitialDataOptions(ls.found, ls.map),
    queryFn: () => fetchSlideOwnerStatsMap(workspaceId, owners, { forceRefresh }),
  });
}
